import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { SingleUserTeamSelector } from '@/react/portainer/policies/components/SingleUserTeamSelector';

import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';
import {
  actionsColumn,
  isIdForNewRow,
} from '@@/datatables/editable/actionsColumn';
import { isEditableTableMeta } from '@@/datatables/editable/isEditableTableMeta';

import { NamespaceSelect } from './NamespaceSelect';
import { KubernetesUnifiedAccess } from './types';

const columnHelper = createColumnHelper<KubernetesUnifiedAccess>();

// Namespaced roles that require namespace specification
const NAMESPACE_ROLE_IDS = [3, 4, 6]; // Standard, Read-only, Namespace Operator

type UserTeamValue = Pick<KubernetesUnifiedAccess, 'Type' | 'Name' | 'PartyId'>;

interface GetColumnsDeps {
  accessList: KubernetesUnifiedAccess[];
  onChange: (value: KubernetesUnifiedAccess[]) => void;
  onRemove: (item: KubernetesUnifiedAccess) => void;
  userOptions: Option<number>[];
  teamOptions: Option<number>[];
  roleOptions: Option<number>[];
}

export function useColumns({
  accessList,
  onChange,
  onRemove,
  userOptions,
  teamOptions,
  roleOptions,
}: GetColumnsDeps) {
  const availableNamespaces = useMemo(
    () => Array.from(new Set(accessList.flatMap((a) => a.namespaces || []))),
    [accessList]
  );
  // Combine users and teams into one list with proper structure
  const usersAndTeamsOptions = [
    ...userOptions.map((u) => ({
      label: u.label,
      value: { Type: 'user' as const, Name: u.label, PartyId: u.value },
    })),
    ...teamOptions.map((t) => ({
      label: t.label,
      value: { Type: 'team' as const, Name: t.label, PartyId: t.value },
    })),
  ];

  return [
    columnHelper.accessor('Name', {
      header: 'Name',
      cell: ({ row: { original, index }, table }) => {
        if (!isEditableTableMeta(table.options.meta)) {
          return null;
        }

        const editableRowIndex = table.options.meta.getEditableRow();
        if (index === editableRowIndex || isIdForNewRow(original.Id)) {
          return (
            <SingleUserTeamSelector<UserTeamValue>
              options={usersAndTeamsOptions}
              value={original}
              onChange={(value) =>
                handleUsersAndTeamsChange(original, value || null)
              }
              data-cy="cy-user-or-team-select"
              getOptionValue={(val) => (val ? `${val.Name}-${val.Type}` : '')}
            />
          );
        }
        return original.Name;
      },
      meta: { width: '15%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('Type', {
      header: 'Type',
      cell: ({ getValue }) => {
        const type = getValue<string>();
        return type.charAt(0).toUpperCase() + type.slice(1);
      },
      meta: { width: '15%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('Role', {
      header: 'Role',
      id: 'role',
      cell: ({ row: { original, index }, table }) => {
        if (!isEditableTableMeta(table.options.meta)) {
          return null;
        }

        const editableRowIndex = table.options.meta.getEditableRow();
        return index === editableRowIndex || isIdForNewRow(original.Id) ? (
          <RoleCell
            item={original}
            roleOptions={roleOptions}
            onChangeRole={handleRoleChange}
          />
        ) : (
          <div>{original.Role.Name}</div>
        );
      },
      meta: { width: '30%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('namespaces', {
      header: 'Namespaces',
      id: 'namespaces',
      cell: ({ row: { original, index }, table }) => {
        if (!isEditableTableMeta(table.options.meta)) {
          return null;
        }

        const editableRowIndex = table.options.meta.getEditableRow();
        return index === editableRowIndex || isIdForNewRow(original.Id) ? (
          <NamespacesCell
            item={original}
            onChange={handleNamespacesChange}
            availableNamespaces={availableNamespaces}
          />
        ) : (
          <div>{`${
            original.namespaces ? original.namespaces.join(', ') : '-'
          }`}</div>
        );
      },
      meta: { width: '40%', className: 'overflow-hidden' },
    }),
    actionsColumn<KubernetesUnifiedAccess>(onRemove),
  ];

  function updateAccess(
    item: KubernetesUnifiedAccess,
    updater: (prev: KubernetesUnifiedAccess) => KubernetesUnifiedAccess
  ) {
    const newAccessList = [...accessList];
    const index = newAccessList.findIndex(
      (access) => access.Type === item.Type && access.PartyId === item.PartyId
    );
    if (index !== -1) {
      newAccessList[index] = updater(newAccessList[index]);
      onChange(newAccessList);
    }
  }

  function handleRoleChange(item: KubernetesUnifiedAccess, newRoleId: number) {
    const roleName =
      roleOptions.find((r) => r.value === newRoleId)?.label || '';
    const wasNamespaceRole =
      item.Role.Id && NAMESPACE_ROLE_IDS.includes(item.Role.Id);
    const isNewNamespaceRole = NAMESPACE_ROLE_IDS.includes(newRoleId);

    updateAccess(item, (prev) => ({
      ...prev,
      Role: { Id: newRoleId, Name: roleName },
      namespaces: (() => {
        if (!isNewNamespaceRole) return undefined;
        return wasNamespaceRole ? item.namespaces : [];
      })(),
    }));
  }

  function handleUsersAndTeamsChange(
    item: KubernetesUnifiedAccess,
    newTeamOrUser: UserTeamValue | null
  ) {
    updateAccess(item, (prev) => ({
      ...prev,
      PartyId: newTeamOrUser?.PartyId || null,
      Name: newTeamOrUser?.Name || '',
      Type: (newTeamOrUser?.Type || '') as unknown as 'user' | 'team',
    }));
  }

  function handleNamespacesChange(
    item: KubernetesUnifiedAccess,
    newNamespaces: string[]
  ) {
    updateAccess(item, (prev) => ({ ...prev, namespaces: newNamespaces }));
  }
}

function RoleCell({
  item,
  roleOptions,
  onChangeRole,
}: {
  item: KubernetesUnifiedAccess;
  roleOptions: Option<number>[];
  onChangeRole: (item: KubernetesUnifiedAccess, newRoleId: number) => void;
}) {
  return (
    <div className="min-w-max">
      <PortainerSelect<number | null>
        value={item.Role.Id}
        onChange={(value) => onChangeRole(item, value || 0)}
        options={roleOptions}
        placeholder="Select a role"
        bindToBody
        data-cy={`role-select-${item.Type}-${item.PartyId}`}
      />
    </div>
  );
}

function NamespacesCell({
  item,
  onChange,
  availableNamespaces,
}: {
  item: KubernetesUnifiedAccess;
  onChange: (item: KubernetesUnifiedAccess, namespaces: string[]) => void;
  availableNamespaces: string[];
}) {
  const hasNamespaceAccess =
    item.Role.Id && NAMESPACE_ROLE_IDS.includes(item.Role.Id);
  if (!hasNamespaceAccess) {
    return <span className="text-muted">-</span>;
  }

  return (
    <NamespaceSelect
      selectedNamespaces={item.namespaces || []}
      onChange={(namespaces) => onChange(item, namespaces)}
      placeholder="e.g. default, production"
      data-cy={`namespaces-input-${item.Type}-${item.PartyId}`}
      namespaces={availableNamespaces}
    />
  );
}
